/**
 * Ein Suchfeld gegen JTL-Stammdaten (Lieferanten, Artikel) - Ersatz fuer die frueher frei
 * eingetippten Felder (Spec, "Bewusste Verhaltensaenderungen", Nr. 3). Kein Vorbild in `index.html`:
 * der Bestand fuellt "Lieferant" in der Anlagemaske aus einem lokalen `<select>`
 * (`supplierSelectOptions()`, `index.html:5017`, gespeist aus `DB.suppliers`) und Artikelpositionen
 * ueber freie Texteingaben oder den Excel-Import (`index.html:7998-7999`). Diese Komponente ist
 * bewusst NICHT gegen die Baseline abgesichert (`test/visual/ausnahmen.ts`, Eintrag
 * `NewOrder/Anlagemaske`, Grund "bewusste-verhaltensaenderung").
 *
 * Nutzt `jtlSuche` (`src/api/jtlSuche.ts`) fuer Debounce/Mindestlaenge/Cache/Single-Flight-Guard
 * (Global Constraint 16) - siehe Kopfkommentar dort zur Vorziehung aus Task 17.
 *
 * ARIA-Combobox-Muster (`role="combobox"` + `role="listbox"` + `role="option"`), Optionen sind
 * NICHT fokussierbare `<li>`-Elemente: ein Klick darauf verschiebt den Fokus nicht weg vom
 * Eingabefeld, ein Blur-Handler kann die Liste deshalb gefahrlos beim Verlassen des Feldes
 * schliessen, ohne der Klick-Auswahl zuvorzukommen.
 *
 * TASK 2 (2026-08-13): Tastatursteuerung (Pfeiltasten, Home/End, Enter, Escape) und Zustandszeilen
 * ("mindestens zwei Zeichen", "keine Treffer", Suchfehler) kommen hinzu - bis dahin liess sich die
 * Liste nur mit der Maus bedienen und blieb bei "zu kurz" oder "nichts gefunden" stumm. Dieselbe
 * Aenderung deckt einen echten Fehler ab: `gedrosselteSuche(text).then(...)` hatte keinen
 * Ablehnungszweig, ein Suchfehler wurde also zur unbehandelten Zurueckweisung.
 */
import { useId, useMemo, useRef, useState, type JSX, type KeyboardEvent, type ReactNode } from "react";
import { JTL_SUCHE_MINDESTLAENGE, jtlSuche, normalisiereSuchbegriff } from "../../api/jtlSuche.ts";
import { fehlertext } from "../../lib/fehlertext.ts";
import { Ikone } from "../Ikone.tsx";

export interface JtlTreffer {
  id: string;
  [feld: string]: unknown;
}

/**
 * Zerlegt `text` am ERSTEN Vorkommen des normalisierten `begriff` (Gross-/Kleinschreibung
 * ignorierend) - der Treffer geht in `<b>`, der Rest bleibt unveraendert. Kommt der Begriff nicht
 * vor (oder ist er leer), bleibt der Text ungeteilt - kein Sonderfall im Markup. `<b>` traegt zum
 * zugaenglichen Namen der Option nichts bei (die Namensberechnung liest Textinhalt, nicht
 * Formatierung), deshalb aendert diese Hervorhebung ihn nicht.
 */
function hervorgehoben(text: string, begriff: string): JSX.Element | string {
  const suchbegriff = normalisiereSuchbegriff(begriff);
  if (suchbegriff === "") return text;
  const stelle = text.toLowerCase().indexOf(suchbegriff);
  if (stelle === -1) return text;
  const vorher = text.slice(0, stelle);
  const treffer = text.slice(stelle, stelle + suchbegriff.length);
  const nachher = text.slice(stelle + suchbegriff.length);
  return (
    <>
      {vorher}
      <b>{treffer}</b>
      {nachher}
    </>
  );
}

export interface JtlSuchfeldProps<T extends JtlTreffer> {
  id: string;
  /**
   * Sichtbarer Text im umschliessenden `<label>` - der Aufrufer rendert `<span>{label}</span>`.
   *
   * BERICHTIGT am 2026-08-13: bis dahin wurde diese Prop entgegengenommen und NIRGENDS benutzt -
   * eine Pflichtangabe ohne Wirkung. Sie traegt jetzt das `aria-label` des Eingabefeldes. Fuer die
   * beiden Bestandsaufrufer aendert das den zugaenglichen Namen nicht (beide uebergeben denselben
   * Text, den ihr umschliessendes `<label>` zeigt).
   */
  label: string;
  /** Roher Serverabruf fuer EINEN Suchbegriff. Wird hier per `jtlSuche` gedrosselt/gecacht. */
  suche: (begriff: string) => Promise<T[]>;
  /** Wie ein Treffer im Eingabefeld UND als Options-Beschriftung erscheint. */
  beschriftung: (treffer: T) => string;
  /**
   * Optionale zweite Zeile rechts in der Option - Kennung, SKU, Zustandsmarke. Rein anzeigend.
   *
   * `ReactNode` STATT `string` SEIT DEM 2026-08-13: die Lieferantensuchen kennzeichnen inaktive
   * Treffer, und eine Marke ist ein eigenes Element mit eigener Klasse, kein Textzusatz. Fuer die
   * bisherigen Aufrufer aendert die Weitung nichts - eine Zeichenkette IST ein `ReactNode`.
   *
   * `null`/`undefined` heisst "kein Zusatz an DIESEM Treffer" und zeichnet auch keine leere Huelle
   * (siehe unten): die Anlagemaske markiert nur die stillgelegten Lieferanten, und eine leere
   * `<span>` an jedem uebrigen Treffer waere eine Randspalte aus lauter Nichts.
   */
  zusatz?: (treffer: T) => ReactNode;
  /** Anzeigetext (kontrolliert). */
  wert: string;
  onWertAendern: (text: string) => void;
  /**
   * Bei Auswahl EINES Treffers mit diesem aufgerufen; bei jeder Texteingabe zuvor mit `null` - die
   * vorherige Auswahl gilt fuer neu eingegebenen Text nicht mehr (Grundlage der
   * "nimmt keinen Lieferanten an, der nicht aus der Suche stammt"-Pruefung im Container).
   */
  onAuswahl: (treffer: T | null) => void;
  platzhalter?: string;
  required?: boolean;
  disabled?: boolean;
}

export function JtlSuchfeld<T extends JtlTreffer>({
  id,
  label,
  suche,
  beschriftung,
  zusatz,
  wert,
  onWertAendern,
  onAuswahl,
  platzhalter,
  required,
  disabled,
}: JtlSuchfeldProps<T>): JSX.Element {
  const [optionen, setOptionen] = useState<T[]>([]);
  const [offen, setOffen] = useState(false);
  const [laeuft, setLaeuft] = useState(false);
  const [fehler, setFehler] = useState<string | null>(null);
  /** Index der per Tastatur hervorgehobenen Option; -1 = keine. */
  const [aktiv, setAktiv] = useState(-1);
  const angefordert = useRef("");
  const listboxId = `${id}-optionen`;
  const reactId = useId();
  const optionId = (index: number): string => `${listboxId}-${index}`;

  // Eine `jtlSuche`-Instanz pro `suche`-Funktion: Cache/Single-Flight-Zustand bleibt ueber
  // Tastenanschlaege hinweg erhalten, solange der Aufrufer dieselbe Funktionsreferenz reicht.
  const gedrosselteSuche = useMemo(() => jtlSuche<T>({ abruf: suche }), [suche]);

  function beiEingabe(text: string): void {
    onWertAendern(text);
    onAuswahl(null);
    setOffen(true);
    setAktiv(-1);
    setFehler(null);
    angefordert.current = text;
    const langGenug = normalisiereSuchbegriff(text).length >= JTL_SUCHE_MINDESTLAENGE;
    setLaeuft(langGenug);
    gedrosselteSuche(text).then(
      (treffer) => {
        // Stale-Guard: nur die Antwort auf die ZULETZT gestellte Eingabe darf die Liste fuellen.
        if (angefordert.current !== text) return;
        setOptionen(treffer);
        setLaeuft(false);
      },
      (grund) => {
        // BIS ZUM 2026-08-13 GAB ES DIESEN ZWEIG NICHT. Ein Suchfehler wurde damit zur
        // unbehandelten Zurueckweisung: die Liste blieb leer, und der Bediener sah nicht, ob nichts
        // gefunden wurde oder die Suche gar nicht lief.
        if (angefordert.current !== text) return;
        setOptionen([]);
        setLaeuft(false);
        // Die Hinweiszeile unter dem Feld gab es schon (Spec R2): neu ist der Text aus `fehlertext()`.
        setFehler(fehlertext(grund, "Suche fehlgeschlagen."));
      },
    );
  }

  function waehle(treffer: T): void {
    onWertAendern(beschriftung(treffer));
    onAuswahl(treffer);
    setOptionen([]);
    setOffen(false);
    setAktiv(-1);
  }

  const zeigeListe = offen && optionen.length > 0;
  const laenge = normalisiereSuchbegriff(wert).length;
  const hinweis: string | null = fehler
    ? fehler
    : laenge > 0 && laenge < JTL_SUCHE_MINDESTLAENGE
      ? `Mindestens zwei Zeichen eingeben.`
      : laenge >= JTL_SUCHE_MINDESTLAENGE && !laeuft && optionen.length === 0
        ? "Keine Treffer."
        : null;
  const zeigeHinweis = offen && !zeigeListe && hinweis !== null;

  function beiTaste(event: KeyboardEvent<HTMLInputElement>): void {
    if (event.key === "Escape") {
      setOffen(false);
      setAktiv(-1);
      return;
    }
    if (!zeigeListe) {
      // Pfeiltaste bei geschlossener, aber gefuellter Liste oeffnet sie wieder.
      if ((event.key === "ArrowDown" || event.key === "ArrowUp") && optionen.length > 0) {
        event.preventDefault();
        setOffen(true);
        setAktiv(event.key === "ArrowDown" ? 0 : optionen.length - 1);
      }
      return;
    }
    if (event.key === "ArrowDown") {
      event.preventDefault();
      setAktiv((alt) => (alt + 1) % optionen.length);
      return;
    }
    if (event.key === "ArrowUp") {
      event.preventDefault();
      setAktiv((alt) => (alt <= 0 ? optionen.length - 1 : alt - 1));
      return;
    }
    if (event.key === "Home") {
      event.preventDefault();
      setAktiv(0);
      return;
    }
    if (event.key === "End") {
      event.preventDefault();
      setAktiv(optionen.length - 1);
      return;
    }
    // ENTER NUR MIT HERVORHEBUNG. Ohne diese Bedingung schluckte das Suchfeld das Absenden des
    // umschliessenden Formulars (Anlagemaske) - eine Nebenwirkung, die kein Aufrufer bestellt hat.
    if (event.key === "Enter" && aktiv >= 0 && aktiv < optionen.length) {
      event.preventDefault();
      waehle(optionen[aktiv]!);
    }
  }

  return (
    <div className="jtl-suchfeld">
      <Ikone name="search" className="jtl-suchfeld-lupe" />
      <input
        id={id}
        role="combobox"
        type="text"
        autoComplete="off"
        aria-label={label}
        aria-autocomplete="list"
        aria-expanded={zeigeListe}
        aria-controls={listboxId}
        aria-activedescendant={zeigeListe && aktiv >= 0 ? optionId(aktiv) : undefined}
        required={required}
        disabled={disabled}
        placeholder={platzhalter}
        value={wert}
        onChange={(event) => beiEingabe(event.target.value)}
        onFocus={() => setOffen(optionen.length > 0 || hinweis !== null)}
        onBlur={() => setOffen(false)}
        onKeyDown={beiTaste}
      />
      {zeigeListe && (
        <ul className="jtl-suchfeld-optionen" role="listbox" id={listboxId}>
          {optionen.map((treffer, index) => {
            // EINMAL ausgewertet und auf "wirklich vorhanden" geprueft, statt zweimal aufgerufen:
            // `zusatz` darf seit dem 2026-08-13 `null` liefern ("kein Zusatz an DIESEM Treffer"),
            // und eine leere `<span class="mono">` waere in der Liste eine Randspalte aus Nichts.
            const beigabe = zusatz ? zusatz(treffer) : null;
            return (
              <li
                key={treffer.id ?? `${reactId}-${index}`}
                id={optionId(index)}
                role="option"
                // `aria-selected` bedeutet hier ab jetzt "per Tastatur hervorgehoben" (das
                // ARIA-Muster fuer eine Listbox mit `aria-activedescendant`), nicht mehr "entspricht
                // dem Feldinhalt". `suchfeld.css` faerbt danach - eine zusaetzliche Klasse braucht
                // es deshalb NICHT.
                aria-selected={index === aktiv}
                // onMouseDown statt onClick: verhindert, dass ein vorheriges onBlur (Fokuswechsel)
                // die Liste schliesst, bevor der Klick ausgewertet wird - Standard-Combobox-Kniff,
                // hier ohnehin unschaedlich, weil <li> nicht fokussierbar ist.
                onMouseDown={(event) => event.preventDefault()}
                onClick={() => waehle(treffer)}
              >
                <span>{hervorgehoben(beschriftung(treffer), wert)}</span>
                {beigabe === null || beigabe === undefined || beigabe === "" ? null : (
                  <span className="mono">{beigabe}</span>
                )}
              </li>
            );
          })}
        </ul>
      )}
      {zeigeHinweis && (
        <p className="suchfeld-hinweis" role={fehler ? "alert" : undefined}>
          {hinweis}
        </p>
      )}
    </div>
  );
}
